import { glossary } from "@/content/glossary"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import React from "react"
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

type SmartTermProps = {
  termKey: keyof typeof glossary
  children?: React.ReactNode
  className?: string
}

export function SmartTerm({ termKey, children, className }: SmartTermProps) {
  const data = glossary[termKey]

  if (!data) return <>{children}</>

  const tooltipContent = (
    <div className="space-y-2.5 max-w-[280px]">
      {/* Term header */}
      <p className={cn(
        semanticTokens.typography.label,
        "font-semibold",
        semanticTokens.fg.default
      )}>
        {data.term}
      </p>

      {/* Definition */}
      <p className={cn(
        semanticTokens.typography.caption,
        semanticTokens.fg.default,
        "leading-snug"
      )}>
        {data.definition}
      </p>

      {/* Analogy */}
      <div className={cn(
        "pt-2.5 border-t",
        semanticTokens.border.subtle
      )}>
        <p className={cn(
          semanticTokens.typography.captionSmall,
          "italic",
          semanticTokens.fg.muted,
          "leading-snug"
        )}>
          {data.analogy}
        </p>
      </div>
    </div>
  )

  return (
    <TooltipProvider delayDuration={300}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "cursor-help inline",
              semanticTokens.motion.fast,
              "underline decoration-dashed underline-offset-4",
              semanticTokens.fg.muted,
              "decoration-slate-400",
              "hover:decoration-blue-500 hover:text-blue-600",
              semanticTokens.focus.ring,
              "focus-visible:rounded-sm",
              "outline-none",
              className
            )}
            tabIndex={0}
            role="button"
            aria-label={`Learn more about ${data.term}`}
          >
            {children || data.term}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          sideOffset={8}
          className="max-w-[280px]"
        >
          {tooltipContent}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}
